import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpenText';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons/faFileCsv';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { Download } from '../../../helpers/download/download';
import { JobResultInterface } from '../../../models/interface/jobresult.interface';
import { catchError, delay, mergeMap, repeat, skip, take } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GetAllJobResultsOfUserPayload, QueueActionTypes } from '../../../store/queue/queue.action';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

export type DialogStudyReportData = {
  group: StudyInterface;
};

@Component({
  selector: 'app-dialog-study-report',
  templateUrl: './dialog-study-report.component.html',
  styleUrls: ['./dialog-study-report.component.scss'],
  providers: [StudyStore],
})
export class DialogStudyReportComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faEnvelopeOpenText = faEnvelopeOpenText;
  faKey = faKey;
  faFileCsv = faFileCsv;
  faTimes = faTimes;
  faEllipsisH = faEllipsisH;
  faRedo = faRedo;
  faClock = faClock;

  public isLoading$: Observable<boolean>;

  // Data provided by StudyService
  public group: StudyInterface;

  public includeMeta = false;
  public includeDiaryInstance = false;

  // Form submission
  public submitted = false;

  public downloadCSVReportResponse = new BehaviorSubject<string>('DEFAULT');

  public download$: Observable<Download>;

  public jobresults$: Observable<JobResultInterface[]>;

  public selectedTabIndex = 0;

  public showDownloadHintSubject = new BehaviorSubject<boolean>(false);

  public pagedJobResults$: Observable<JobResultInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private pagedJobResultsSubject = new BehaviorSubject<JobResultInterface[]>([]);
  private jobresultsSubject = new BehaviorSubject<JobResultInterface[]>([]);

  private getStudyCSVReportQueueResponse$: Observable<any>;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogStudyReportData,
    private helperService: HelperService,
    private studyStore: StudyStore,
    private actions$: Actions,
    private store: Store,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.jobresults$ = this.jobresultsSubject.asObservable();
    this.pagedJobResults$ = this.pagedJobResultsSubject.asObservable();
    this.getStudyCSVReportQueueResponse$ = this.studyStore.getStudyCSVReportQueueResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.group = this.data.group;

    const poll = of({}).pipe(
      mergeMap(() => {
        if (this.jobresultsSubject.value.some((job) => job.attributes.finished === 0)) {
          this.loadJobResults();
        }
        return of({});
      }),
      catchError(() => of(null)),
      delay(30000),
      repeat(),
    );

    this.subscriptions.push(poll.subscribe());

    this.loadJobResults();
  }

  public getInstanceReportCSV(group: StudyInterface) {
    const responseState = this.downloadCSVReportResponse.value;
    if (responseState === 'FAILURE' || responseState === 'SUCCESS') {
      this.downloadCSVReportResponse.next('DEFAULT');
      return;
    }
    this.showDownloadHintSubject.next(false);
    const meta = this.includeMeta ? 1 : undefined;
    const diaryInstance = this.includeDiaryInstance ? 1 : undefined;
    this.submitted = true;
    if (responseState === 'DEFAULT') {
      this.downloadCSVReportResponse.next('LOADING');
      this.studyStore.getStudyCSVReportQueue({
        studyId: group.id,
        meta,
        diaryInstances: diaryInstance,
      });
      this.getStudyCSVReportQueueResponse$
        .pipe(
          skip(1),
          take(1),
          mergeMap(
            (result: any) => iif(
              () => result instanceof HttpErrorResponse,
              throwError(() => result),
              of(result),
            ),
          ),
        )
        .subscribe({
          next: () => {
            this.loadJobResults();
            this.downloadCSVReportResponse.next('SUCCESS');
            this.showDownloadHintSubject.next(true);
            setTimeout(() => {
              this.downloadCSVReportResponse.next('DEFAULT');
              this.submitted = false;
            }, 2500);
          },
          error: () => {
            this.downloadCSVReportResponse.next('FAILURE');
            setTimeout(() => {
              this.downloadCSVReportResponse.next('DEFAULT');
              this.submitted = false;
            }, 2500);
          },
        });
    }
  }

  public setSelectedTabIndex(event: number): void {
    this.selectedTabIndex = event;
    this.showDownloadHintSubject.next(false);
  }

  public loadJobResults(): void {
    const payload: GetAllJobResultsOfUserPayload = {
      jobType: 'Study',
      entityId: this.group.id,
    };
    this.store.dispatch({ type: QueueActionTypes.getAllJobResultsOfUserType, payload });
    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(
            QueueActionTypes.getAllJobResultsOfUserSuccessType,
            QueueActionTypes.getAllJobResultsOfUserErrorType,
          ),
          take(1),
        )
        .subscribe((result: any) => {
          if (result.type === QueueActionTypes.getAllJobResultsOfUserSuccessType) {
            const allJobs: JobResultInterface[] = result['response']['body']['data'];
            const sortedJobs = allJobs.sort(this.helper.sortByIdDesc);
            this.jobresultsSubject.next(sortedJobs);
            this.pagedJobResultsSubject.next(this.jobresultsSubject.value.slice(0, 20));
            this.isLoadingSubject.next(false);
            this.paginator?.paginator?.firstPage();
          }
        }),
    );
  }

  public updatePagedJobStudies(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobResultsSubject.next(event);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
